import chunk from 'lodash/chunk'
import flatten from 'lodash/flatten'
import type {ParserT} from '@freckle/parser'
import {number, string, array, record, nullable, Parser} from '@freckle/parser'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {providersOrNoneToQueryFilter} from '@freckle/school/ts/manage-teachers/api-helpers/filters'

export type ProficiencyReportStudentDataT = {
  id: number
  standardName: string | undefined | null
}

const studentGroupParser = array(
  record({
    id: number(),
    standardName: nullable(string())
  })
) as ParserT<ProficiencyReportStudentDataT[]>

export type ProficiencyReportTeacherGroupDataT = {
  teacherId: number
  withinGradeLevel: number
  belowGradeLevel: number
  aboveGradeLevel: number
  averageGradeLevel: number | undefined | null
}

export type ProficiencyReportStudentGroupsT = {
  insufficientData: Array<ProficiencyReportStudentDataT>
  withinGradeLevel: Array<ProficiencyReportStudentDataT>
  aboveGradeLevel: Array<ProficiencyReportStudentDataT>
  belowGradeLevel: Array<ProficiencyReportStudentDataT>
}

export type ProficiencyReportAttrs = {
  averageGradeLevel: number | undefined | null
  schoolId: number
  studentGroups: ProficiencyReportStudentGroupsT
  teacherGroups: Array<ProficiencyReportTeacherGroupDataT>
}

const proficiencyReportParser = record({
  averageGradeLevel: nullable(number()),
  schoolId: number(),
  studentGroups: record({
    insufficientData: studentGroupParser,
    withinGradeLevel: studentGroupParser,
    aboveGradeLevel: studentGroupParser,
    belowGradeLevel: studentGroupParser
  }),
  teacherGroups: array(
    record({
      teacherId: number(),
      withinGradeLevel: number(),
      aboveGradeLevel: number(),
      belowGradeLevel: number(),
      averageGradeLevel: nullable(number())
    })
  )
}) as ParserT<ProficiencyReportAttrs>

export function parseProficiencyReports(response: unknown): Array<ProficiencyReportAttrs> {
  return Parser.run<Array<ProficiencyReportAttrs>>(response, array(proficiencyReportParser))
}

type ExtraDataT = {
  grade: number
  domainId: RlDomainIdT
  teacherId?: number
  identityProviders?: Array<IdentityProviderOrNoneT>
}

const PROFICENCY_REPORT_SCHOOL_BATCH_SIZE = 3

export async function fetchProficiencyReportsForSchools(
  schoolIds: Array<number>,
  {grade, domainId, identityProviders, teacherId}: ExtraDataT
): Promise<Array<ProficiencyReportAttrs>> {
  function fetchChunk(schoolIds: number[]) {
    return fancyClient
      .GET(
        '/2/schools/{SchoolsMathAdaptiveGradeLevelReportR-NonEmptySchoolId}/reports/math-adaptive-grade-level',
        {
          params: {
            path: {
              'SchoolsMathAdaptiveGradeLevelReportR-NonEmptySchoolId': schoolIds
            },
            query: {
              grade,
              'domain-id': domainId,
              'teacher-id': teacherId,
              ...(identityProviders ? providersOrNoneToQueryFilter(identityProviders) : {})
            }
          }
        }
      )
      .then(extractData)
      .then(parseProficiencyReports)
  }

  const batches = chunk(schoolIds, PROFICENCY_REPORT_SCHOOL_BATCH_SIZE).map(fetchChunk)

  return flatten(await Promise.all(batches))
}
